// src/services/printer/printerConfigService.js

// Constantes para las claves de almacenamiento
const STORAGE_KEYS = {
  PRINTERS: "printers",
  CONFIGURATIONS: "printerConfigurations",
  CONNECTIONS: "printerConnections",
};

// Tipos de destino de impresión
const PRINT_DESTINATIONS = {
  KITCHEN: "Cocina",
  BAR: "Barra",
  CUSTOMER: "Cliente",
};

// Estructura base para la configuración de una impresora
const DEFAULT_PRINTER_CONFIG = {
  printKitchenOrders: false,
  printBarOrders: false,
  autoCustomerBills: false,
  isDefault: false,
  isEnabled: true,
  retryAttempts: 3,
  retryDelay: 1000,
};

class PrinterConfigService {
  constructor() {
    this.printers = new Map();
    this.loadConfigurations();
    console.log("After loading configurations:");
    this.debugPrinters();
  }

  debugPrinters() {
    console.log("Debug Printers:");
    console.log("Map size:", this.printers.size);
    this.printers.forEach((printer, id) => {
      console.log("Printer:", {
        id,
        name: printer.name,
        config: printer.config,
        connection: printer.connection,
      });
    });
  }

  // Cargar todas las configuraciones almacenadas
  loadConfigurations() {
    try {
      // Obtener datos del localStorage
      const storedData = localStorage.getItem("connectedPrinters");
      const storedConfigs = localStorage.getItem("printerConfigurations");

      console.log("Stored Data:", storedData);
      console.log("Stored Configs:", storedConfigs);

      if (storedData) {
        const printers = JSON.parse(storedData);
        const configs = storedConfigs ? JSON.parse(storedConfigs) : {};

        // Cargar cada impresora en el Map
        printers.forEach((printer) => {
          const fullPrinter = {
            ...printer,
            config: configs[printer.id] || {
              printKitchenOrders: false,
              printBarOrders: false,
              autoCustomerBills: false,
              isEnabled: true,
            },
          };
          this.printers.set(printer.id, fullPrinter);
        });
      }

      console.log("Printers loaded:", this.getAllPrinters());
    } catch (error) {
      console.error("Error loading configurations:", error);
    }
  }

  // Obtener la configuración específica de una impresora
  getConfigForPrinter(printerId) {
    try {
      const storedConfigs = localStorage.getItem(STORAGE_KEYS.CONFIGURATIONS);
      const configs = storedConfigs ? JSON.parse(storedConfigs) : {};
      return {
        ...DEFAULT_PRINTER_CONFIG,
        ...(configs[printerId] || {}),
      };
    } catch (error) {
      console.error(`Error getting config for printer ${printerId}:`, error);
      return { ...DEFAULT_PRINTER_CONFIG };
    }
  }

  // Obtener el estado de conexión de una impresora
  getConnectionForPrinter(printerId) {
    try {
      const storedConnections = localStorage.getItem(STORAGE_KEYS.CONNECTIONS);
      const connections = storedConnections
        ? JSON.parse(storedConnections)
        : {};
      return (
        connections[printerId] || { isConnected: false, lastConnected: null }
      );
    } catch (error) {
      console.error(
        `Error getting connection for printer ${printerId}:`,
        error
      );
      return { isConnected: false, lastConnected: null };
    }
  }

  // Actualizar la configuración de una impresora
  updatePrinterConfig(printerId, config) {
    try {
      const printer = this.printers.get(printerId);
      if (!printer) {
        throw new Error("Printer not found");
      }

      printer.config = {
        ...printer.config,
        ...config,
      };

      this.printers.set(printerId, printer);
      this.saveToLocalStorage();

      console.log("Config updated for printer:", printerId, printer.config);
      return true;
    } catch (error) {
      console.error("Error updating printer config:", error);
      return false;
    }
  }

  // Actualizar el estado de conexión de una impresora
  updatePrinterConnection(printerId, isConnected) {
    try {
      const printer = this.printers.get(printerId);
      if (!printer) {
        throw new Error(`Printer ${printerId} not found`);
      }

      const connection = {
        isConnected,
        lastConnected: isConnected ? new Date().toISOString() : null,
      };

      printer.connection = connection;
      this.printers.set(printerId, printer);
      this.saveConnections();

      return true;
    } catch (error) {
      console.error(`Error updating printer ${printerId} connection:`, error);
      return false;
    }
  }

  // Agregar una nueva impresora
  addPrinter(printerInfo) {
    try {
      // Crear objeto de impresora completo
      const printer = {
        id: printerInfo.id,
        name: printerInfo.name,
        config: {
          printKitchenOrders: true,
          printBarOrders: true,
          autoCustomerBills: true,
          isEnabled: true,
        },
      };

      // Agregar al Map
      this.printers.set(printer.id, printer);

      // Guardar en localStorage
      this.saveToLocalStorage();

      console.log("Printer added:", printer);
      return printer;
    } catch (error) {
      console.error("Error adding printer:", error);
      throw error;
    }
  }

  saveToLocalStorage() {
    try {
      const printersArray = Array.from(this.printers.values());
      const configs = {};

      printersArray.forEach((printer) => {
        configs[printer.id] = printer.config;
      });

      localStorage.setItem("connectedPrinters", JSON.stringify(printersArray));
      localStorage.setItem("printerConfigurations", JSON.stringify(configs));

      console.log("Saved to localStorage:", {
        printers: printersArray,
        configs,
      });
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }

  // Eliminar una impresora
  removePrinter(printerId) {
    try {
      const result = this.printers.delete(printerId);
      if (result) {
        this.saveToLocalStorage();
      }
      return result;
    } catch (error) {
      console.error("Error removing printer:", error);
      return false;
    }
  }

  // Guardar todas las configuraciones
  saveConfigurations() {
    try {
      const printersArray = Array.from(this.printers.values());
      const configs = {};

      // Extraer configuraciones
      printersArray.forEach((printer) => {
        configs[printer.id] = printer.config;
      });

      // Guardar en localStorage
      localStorage.setItem(
        STORAGE_KEYS.PRINTERS,
        JSON.stringify(printersArray)
      );
      localStorage.setItem(
        STORAGE_KEYS.CONFIGURATIONS,
        JSON.stringify(configs)
      );

      console.log("Configurations saved:", {
        printers: printersArray,
        configs,
      });
    } catch (error) {
      console.error("Error saving configurations:", error);
    }
  }

  getPrinterConfig(printerId) {
    try {
      const printer = this.printers.get(printerId);
      if (!printer) {
        console.warn(`Printer ${printerId} not found`);
        return null;
      }
      return printer.config;
    } catch (error) {
      console.error("Error getting printer config:", error);
      return null;
    }
  }

  // Guardar solo las conexiones
  saveConnections() {
    try {
      const connections = {};
      this.printers.forEach((printer, id) => {
        connections[id] = printer.connection;
      });
      localStorage.setItem(
        STORAGE_KEYS.CONNECTIONS,
        JSON.stringify(connections)
      );
    } catch (error) {
      console.error("Error saving printer connections:", error);
    }
  }

  // Obtener todas las impresoras
  getAllPrinters() {
    return Array.from(this.printers.values());
  }

  // Obtener impresoras por destino
  getPrintersByDestination(destination) {
    // return this.getAllPrinters().filter(
    //   (printer) => printer.config[`print${destination}Orders`]
    // );
    console.log(
      "[getPrintersByDestination] Buscando impresoras para:",
      destination
    );

    return this.getAllPrinters().filter((printer) => {
      console.log("[getPrintersByDestination] ", printer);
      switch (destination) {
        case "Cocina":
          return printer.config.printKitchenOrders;
        case "Barra":
          return printer.config.printBarOrders;
        case "Cliente":
          return printer.config.autoCustomerBills;
        default:
          return false;
      }
    });
  }

  // Obtener la impresora por defecto para un destino específico
  getDefaultPrinterForDestination(destination) {
    return this.getPrintersByDestination(destination).find(
      (printer) => printer.config.isDefault
    );
  }

  // Resetear a configuración por defecto
  resetToDefaults() {
    this.printers.clear();
    localStorage.removeItem(STORAGE_KEYS.PRINTERS);
    localStorage.removeItem(STORAGE_KEYS.CONFIGURATIONS);
    localStorage.removeItem(STORAGE_KEYS.CONNECTIONS);
  }

  // Validar si una impresora está disponible para un destino específico
  isPrinterAvailableForDestination(printerId, destination) {
    const printer = this.printers.get(printerId);
    if (!printer) return false;

    return (
      printer.config.isEnabled &&
      printer.connection.isConnected &&
      printer.config[`print${destination}Orders`]
    );
  }
}

export const printerConfigService = new PrinterConfigService();
export { PRINT_DESTINATIONS };
